import React, { useContext } from "react";

// What every part of the editor needs and none of them can hold for itself.
//
// `sections` is the list the section picker offers. It is queried ONCE, at the
// top of the screen, because the picker lives inside a list widget inside a
// form: a query in that position re-runs on every render of every row, and its
// answer's identity is what the form's `config` is memoised on — so it would
// rebuild the widget tree on each keystroke and detach the control being typed
// into (P139).
//
// `locales` is the site's declared languages, and it is here for exactly the
// same reason `sections` is: the Page tab's locale picker offers them, that
// picker's option list is what the tab's form `config` is built from, and a
// query inside the tab would hand it a new answer identity on every render.
// One query at the top of the screen, one memo, one config.
//
// An EMPTY list is the honest answer for a site that declares no languages and
// for one whose query has not answered yet, and the Page tab renders the plain
// text field in that case rather than a picker with nothing in it.
//
// `languages` is the OTHER languages this page exists in, as codes — the
// translation group, minus this page. The Elements tab names them in the
// sentence that says what a structural change is about to do, and it is on this
// context for the same reason the two lists above are: it comes from a query at
// the top of the screen, and a query inside the tab would hand the tab a new
// answer identity on every render (P139).
//
// EMPTY is the ordinary case, not a failure: it is every page nobody has
// translated, and the tab says nothing at all rather than saying "shared with
// no other languages".
//
// `openElement` and `closeElement` NAVIGATE. An element is a route —
// `/edit/element/:index`, the way gen-2 addressed one — and not state.
//
// It WAS state, for as long as P144 was open: every screen in this app remounted
// its whole subtree on any navigation, because each route RENDERED its render
// prop as an element and the arrow arriving there is a new value — so a new
// component type — on every render. For a screen you navigate away from that is
// invisible; this one navigates within itself, so opening an element re-read the
// form from the saved row and the element you had just added was gone.
//
// The routes CALL that prop now — `{Children({ base, basepath, params })}` — so a
// navigation inside the editor updates rather than remounts, and an element can
// have a URL again. See routes.test.js, which is what keeps it that way.
//
// They live on this context rather than being a `useHistory` call at each call
// site because opening an element also has to bring the Elements tab forward:
// the preview's double-click can arrive while the Page tab is open, and the tab
// strip's state lives above every one of them.
const Context = React.createContext({
  siteId: null,
  pageId: null,
  sections: [],
  collections: [],
  locales: [],
  languages: [],
  openElement: () => {},
  closeElement: () => {},
});

export const useEditorPage = () => useContext(Context);

export const Provider = Context.Provider;
